import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { loadWoStatuses, textColorOn } from "utils/woStatus";

/**
 * Settings -> WO Statuses (user, 2026-10-29). The list drives every WO status
 * dropdown, the W Orders tabs (All + these, in this order) and the Job Board
 * tabs. Renaming keeps the stored value, so work orders follow the new name.
 * A status still on a work order cannot be deleted (the API says how many).
 */
const EMPTY = { id: null, name: "", color: "#6c757d", lockCustomerApproval: false };

export default function WoStatuses() {
  const dispatch = useDispatch();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // EMPTY for add, a row for edit
  const [saving, setSaving] = useState(false);

  const notify = (type, message) => dispatch(setNotification({ type, message }));
  const errorOf = (err) => err?.response?.data?.message || err.message;

  const refresh = async () => {
    setLoading(true);
    try {
      setStatuses(await loadWoStatuses(true));
    } catch (err) {
      notify("error", errorOf(err));
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        id: editing.id,
        name: editing.name,
        color: editing.color,
        lockCustomerApproval: editing.lockCustomerApproval,
      };
      const { data } = editing.id
        ? await API.patch("/settings/wo-status", body, { headers: authHeader() })
        : await API.post("/settings/wo-status", body, { headers: authHeader() });
      notify("success", data?.message || "Saved");
      setEditing(null);
      await refresh();
    } catch (err) {
      notify("error", errorOf(err));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      const { data } = await API.delete(`/settings/wo-status/${id}`, { headers: authHeader() });
      notify("success", data?.message || "Deleted");
      await refresh();
    } catch (err) {
      notify("error", errorOf(err));
    }
  };

  const move = async (id, direction) => {
    try {
      await API.post("/settings/wo-status/move", { id, direction }, { headers: authHeader() });
      await refresh();
    } catch (err) {
      notify("error", errorOf(err));
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h4 className="m-0">WO Statuses</h4>
          <div className="text-muted small">
            Used in every work order status dropdown, the W Orders tabs and the Job Board tabs, in
            this order.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({ ...EMPTY })}>
          Add Status
        </button>
      </div>

      {loading && !statuses.length ? (
        <Spinner />
      ) : (
        <table className="table table-sm table-hover align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Status</th>
              <th className="text-center">Locks customer approval</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {statuses.map((s, i) => (
              <tr key={s.id}>
                <td>{i + 1}</td>
                <td>
                  <span
                    className="badge px-2 py-1"
                    style={{ backgroundColor: s.color, color: textColorOn(s.color) }}
                  >
                    {s.name}
                  </span>
                </td>
                <td className="text-center">
                  {s.lockCustomerApproval ? (
                    <i className="mdi mdi-lock text-danger fs-5" title="Locked" />
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td className="text-end text-nowrap">
                  <button
                    className="action-icon btn btn-sm border-0"
                    disabled={i === 0}
                    onClick={() => move(s.id, "up")}
                    aria-label={`Move ${s.name} up`}
                  >
                    <i className="mdi mdi-chevron-double-up" />
                  </button>
                  <button
                    className="action-icon btn btn-sm border-0"
                    disabled={i === statuses.length - 1}
                    onClick={() => move(s.id, "down")}
                    aria-label={`Move ${s.name} down`}
                  >
                    <i className="mdi mdi-chevron-double-down" />
                  </button>
                  <button
                    className="action-icon btn btn-sm border-0"
                    onClick={() => setEditing({ ...s })}
                    aria-label={`Edit ${s.name}`}
                  >
                    <i className="mdi mdi-pencil" />
                  </button>
                  <DeleteModalButton
                    btnClass="action-icon btn btn-sm border-0 text-danger"
                    deleteFunc={remove}
                    deleteParams={s.id}
                    confirmText={`Delete the status "${s.name}"?`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal show={Boolean(editing)} onHide={() => setEditing(null)} centered>
        {editing && (
          <form onSubmit={save}>
            <Modal.Header closeButton>
              <Modal.Title as="h5">{editing.id ? "Edit Status" : "Add Status"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <label className="form-label" htmlFor="wo-status-name">
                  Name
                </label>
                <input
                  id="wo-status-name"
                  className="form-control"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  autoFocus
                  required
                />
              </div>
              <div className="mb-3 d-flex align-items-center gap-2">
                <label className="form-label m-0" htmlFor="wo-status-color">
                  Colour
                </label>
                <input
                  id="wo-status-color"
                  type="color"
                  className="form-control form-control-color"
                  value={editing.color}
                  onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                />
                <span
                  className="badge px-2 py-1"
                  style={{ backgroundColor: editing.color, color: textColorOn(editing.color) }}
                >
                  {editing.name || "Preview"}
                </span>
              </div>
              <div className="form-check">
                <input
                  id="wo-status-lock"
                  type="checkbox"
                  className="form-check-input"
                  checked={editing.lockCustomerApproval}
                  onChange={(e) => setEditing({ ...editing, lockCustomerApproval: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="wo-status-lock">
                  Locks customer approval
                  <div className="text-muted small">
                    The customer's approval link is locked while a work order has this status.
                  </div>
                </label>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-light" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
}
